/* iter55f93 — Quickstart Guide.
 *
 * Right-side pinnable panel that coaches a writer through the whole
 * The Loom workflow, from starting a project to publishing to the
 * Bookstore. Each step has:
 *   • A short description
 *   • A "Go" action (optional) that navigates to the right screen or
 *     opens the relevant dialog / panel
 *   • A checkbox — auto-detected where the app already tracks the
 *     signal (e.g. hasProject, hasChapters), manually toggleable
 *     otherwise. Manual completion is persisted in localStorage so
 *     writers can walk in and out of the guide without losing state.
 *
 * Pin behaviour: when pinned, the guide stays visible on top of the
 * workspace (fixed-position, elevated z-index) so a writer can keep
 * it on-screen while typing in the manuscript. When unpinned it
 * still floats but with a lower z-index and can be dismissed by
 * clicking outside.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  X, Pin, PinOff, Compass, CheckCircle2, Circle, ChevronRight,
  ChevronDown, Sparkles, ExternalLink, RotateCcw,
} from 'lucide-react';
import { usePW } from '@/lib/store';
import { cn } from '@/lib/utils';

const STORAGE_KEY = 'pw_quickstart_state_v1';
// iter55g2 — Panel geometry. The guide is a floating card that can be
// dragged (via header) and resized (via bottom-right handle). Values
// are persisted so writers who park it in a corner find it there
// next session. `left`/`top` are viewport-relative pixels.
const DEFAULT_WIDTH  = 448;
const DEFAULT_HEIGHT = 620;
const MIN_WIDTH  = 340;
const MIN_HEIGHT = 320;
function initialGeometry() {
  if (typeof window === 'undefined') return { left: 100, top: 60, width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT };
  const vw = window.innerWidth || DEFAULT_WIDTH + 200;
  const vh = window.innerHeight || DEFAULT_HEIGHT + 100;
  return {
    left:   Math.max(12, vw - DEFAULT_WIDTH - 24),
    top:    Math.max(12, Math.min(80, vh - DEFAULT_HEIGHT - 12)),
    width:  DEFAULT_WIDTH,
    height: Math.min(DEFAULT_HEIGHT, vh - 24),
  };
}

/* ---------- Step catalog ---------- */
/* Each step is `{ id, title, body, cta?, auto? }`.
 * `auto(ctx)` — if returns true the step counts as done even without
 * a manual tick. `ctx` = { hasProject, hasFramework, hasChapters,
 *                          hasDraft, hasBible, hasCover, isPublished }.
 * `cta` — { label, action(handlers) } where handlers exposes the
 * high-level actions the guide is allowed to trigger.
 */
const STEPS = [
  {
    id: 'create-project',
    title: 'Create your first project',
    body: 'Give your novel a title and author name. You can always change them later.',
    cta: { label: 'New project', action: (h) => h.openDialog('newProject') },
    auto: (ctx) => ctx.hasProject,
  },
  {
    id: 'framework',
    title: 'Draft a Framework (optional but recommended)',
    body: 'Use the Novel Framework Builder to lay out logline, story bible, characters (with traits), locations, and chapter transitions before the AI drafts a word.',
    cta: { label: 'Open Framework', action: (h) => h.navigate('/framework') },
    auto: (ctx) => ctx.hasFramework,
  },
  {
    id: 'characters',
    title: 'Populate the Character Bible',
    body: 'Add at least a protagonist, antagonist, and foil. Give each one a want, need, and flaw plus a few trait chips — Claude uses these on every chapter.',
    cta: { label: 'Open Bible', action: (h) => h.togglePanel('bible', true) },
    auto: (ctx) => ctx.hasBible,
  },
  {
    id: 'chapters-outline',
    title: 'Outline your chapters',
    body: 'From the Chapters panel, sketch a 150-500 word transition for each chapter. This is the scene brief the AI reads before drafting.',
    cta: { label: 'Open Chapters', action: (h) => h.togglePanel('chapters', true) },
    auto: (ctx) => ctx.hasChapters,
  },
  {
    id: 'draft-first-chapter',
    title: 'Draft the first chapter',
    body: 'Generate Chapter 1 (or write it by hand) to break the blank-page barrier. The Coach Hints will surface once there\'s prose on the page.',
    cta: { label: 'Go to editor', action: (h) => h.togglePanel('editor', true) },
    auto: (ctx) => ctx.hasDraft,
  },
  {
    id: 'analyze-transitions',
    title: 'Analyze story & transitions',
    body: 'Once you have 2+ chapters drafted, run the transition analyzer. Weak transitions (< 65) can be one-click fixed as tracked-changes.',
    cta: { label: 'Open Chapters', action: (h) => h.togglePanel('chapters', true) },
  },
  {
    id: 'voice-coach',
    title: 'Run the Voice Coach',
    body: 'Audit prose voice against your declared Story Bible voice, then run a per-character dialogue audit. Every flagged line comes with a rewrite you can accept as a tracked change.',
    cta: { label: 'Open Chapters', action: (h) => h.togglePanel('chapters', true) },
  },
  {
    id: 'copyscape',
    title: 'Run a Copyscape scan',
    body: 'Before you publish, check your manuscript for accidental echoes of other work. One scan runs against the whole novel.',
    cta: { label: 'File → Copyscape', action: (h) => h.openDialog('help') },
  },
  {
    id: 'cover',
    title: 'Design a cover in Canva',
    body: 'Connect your Canva account (BYO), then generate KDP-safe cover art from your book\'s metadata.',
    cta: { label: 'Canva cover', action: (h) => h.openDialog('canva') },
    auto: (ctx) => ctx.hasCover,
  },
  {
    id: 'pdf-export',
    title: 'Export a print-ready PDF',
    body: 'Adobe PDF Services builds your KDP interior (choose a trim size). Full-novel exports run in the background — you\'ll receive an email when the download link is ready.',
    cta: { label: 'File → Export', action: (h) => h.openDialog('export') },
  },
  {
    id: 'kdp-bundle',
    title: 'Build a KDP bundle',
    body: 'Zips your interior PDF + Canva cover + metadata JSON into a single upload for Amazon KDP.',
    cta: { label: 'File → Export', action: (h) => h.openDialog('export') },
  },
  {
    id: 'publish-bookstore',
    title: 'Publish to the Bookstore',
    body: 'When you\'re ready to sell, list your book in the Crown & Rose storefront. Set a price, upload the cover, and go live.',
    cta: { label: 'Publish', action: (h) => h.openDialog('publish') },
    auto: (ctx) => ctx.isPublished,
  },
];

function loadState() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const base = { manual: {}, pinned: false, expanded: null, geom: null };
    if (!raw) return base;
    return { ...base, ...JSON.parse(raw) };
  } catch { return { manual: {}, pinned: false, expanded: null, geom: null }; }
}
function saveState(s) {
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); }
  catch { /* localStorage blocked */ }
}

// iter55g2 — Clamp geometry inside the viewport so a rotate / resize
// can't leave the panel unreachable.
function clampGeom(g) {
  if (typeof window === 'undefined') return g;
  const vw = window.innerWidth || DEFAULT_WIDTH + 200;
  const vh = window.innerHeight || DEFAULT_HEIGHT + 100;
  const w = Math.min(Math.max(g.width, MIN_WIDTH), vw - 12);
  const h = Math.min(Math.max(g.height, MIN_HEIGHT), vh - 12);
  const left = Math.min(Math.max(g.left, 0), Math.max(0, vw - w));
  const top  = Math.min(Math.max(g.top, 0), Math.max(0, vh - h));
  return { left, top, width: w, height: h };
}

export default function QuickstartGuide({ open, onClose, handlers }) {
  const { project, projects, chapters } = usePW();
  const [state, setState] = useState(loadState);
  // iter55g2 — Draggable / resizable geometry. Persisted so the panel
  // stays where the writer parked it.
  const [geom, setGeom] = useState(() => clampGeom(loadState().geom || initialGeometry()));
  const dragRef   = React.useRef(null);   // { startX, startY, startLeft, startTop }
  const resizeRef = React.useRef(null);   // { startX, startY, startW, startH }

  useEffect(() => { saveState({ ...state, geom }); }, [state, geom]);

  // Keep geometry inside the viewport on window resize.
  useEffect(() => {
    const onResize = () => setGeom((g) => clampGeom(g));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ------- Drag / resize handlers (pointer events, no external deps) -------
  const onHeaderPointerDown = (e) => {
    // Only left mouse / primary pointer, and never on interactive controls.
    if (e.button !== 0) return;
    if (e.target.closest('[data-drag-ignore]')) return;
    e.preventDefault();
    dragRef.current = {
      startX: e.clientX, startY: e.clientY,
      startLeft: geom.left, startTop: geom.top,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
    document.body.style.userSelect = 'none';
  };
  const onHeaderPointerMove = (e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setGeom((g) => clampGeom({
      ...g,
      left: dragRef.current.startLeft + dx,
      top:  dragRef.current.startTop  + dy,
    }));
  };
  const onHeaderPointerUp = (e) => {
    dragRef.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    document.body.style.userSelect = '';
  };

  const onResizePointerDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = {
      startX: e.clientX, startY: e.clientY,
      startW: geom.width, startH: geom.height,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
    document.body.style.userSelect = 'none';
  };
  const onResizePointerMove = (e) => {
    if (!resizeRef.current) return;
    const dx = e.clientX - resizeRef.current.startX;
    const dy = e.clientY - resizeRef.current.startY;
    setGeom((g) => clampGeom({
      ...g,
      width:  resizeRef.current.startW + dx,
      height: resizeRef.current.startH + dy,
    }));
  };
  const onResizePointerUp = (e) => {
    resizeRef.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    document.body.style.userSelect = '';
  };

  const ctx = useMemo(() => {
    const hasProject   = (projects?.length || 0) > 0;
    const hasChapters  = (chapters?.length || 0) > 0;
    const hasDraft     = (chapters || []).some((c) => (c.words || 0) > 100);
    const hasBible     = !!(project?.story_bible &&
      (project.story_bible.story_arc || project.story_bible.mood ||
       (Array.isArray(project.story_bible.themes) && project.story_bible.themes.length)));
    const hasFramework = !!project?.from_framework_id;
    const hasCover     = !!project?.canva_cover_url;
    const isPublished  = !!project?.bookstore_listing_id;
    return { hasProject, hasChapters, hasDraft, hasBible, hasFramework, hasCover, isPublished };
  }, [project, projects, chapters]);

  const isDone = (step) => !!state.manual[step.id] || (step.auto ? step.auto(ctx) : false);
  const doneCount = STEPS.filter(isDone).length;
  const pct = Math.round((doneCount / STEPS.length) * 100);

  const toggleDone = (id) => setState((s) => ({ ...s, manual: { ...s.manual, [id]: !s.manual[id] } }));
  const setExpanded = (id) => setState((s) => ({ ...s, expanded: s.expanded === id ? null : id }));
  const togglePin = () => setState((s) => ({ ...s, pinned: !s.pinned }));
  const resetProgress = () => {
    if (!window.confirm('Reset all Quickstart progress? Auto-detected steps stay checked.')) return;
    setState((s) => ({ ...s, manual: {} }));
  };

  if (!open) return null;

  const nextStep = STEPS.find((s) => !isDone(s));
  const pinned = !!state.pinned;

  return (
    <>
      {/* Backdrop only when unpinned — pinned mode lets writers keep working. */}
      {!pinned && (
        <button
          type="button"
          aria-label="Close quickstart"
          onClick={onClose}
          className="fixed inset-0 z-30 bg-black/25"
          data-testid="quickstart-backdrop"
        />
      )}
      <aside
        data-testid="quickstart-guide"
        className="fixed z-40 flex flex-col overflow-hidden rounded-lg border border-panel-border bg-panel text-foreground shadow-2xl"
        style={{
          left:   geom.left,
          top:    geom.top,
          width:  geom.width,
          height: geom.height,
          // iter55g2 — fully opaque background so it never blocks
          // manuscript text with see-through glass.
          backgroundColor: 'hsl(var(--panel))',
          boxShadow: '0 18px 48px rgba(0,0,0,0.35), 0 2px 8px rgba(0,0,0,0.18)',
        }}
      >
        <header
          data-testid="quickstart-drag-handle"
          onPointerDown={onHeaderPointerDown}
          onPointerMove={onHeaderPointerMove}
          onPointerUp={onHeaderPointerUp}
          onPointerCancel={onHeaderPointerUp}
          className="flex shrink-0 cursor-move select-none items-center justify-between border-b border-panel-border bg-panel-header px-3 py-2"
          style={{ touchAction: 'none' }}
        >
          <div className="flex items-center gap-2">
            <Compass className="h-4 w-4 text-primary" />
            <span className="text-[12.5px] font-semibold">Quickstart guide</span>
            <span
              className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary"
              data-testid="quickstart-progress-pill"
              title={`${doneCount} of ${STEPS.length} steps complete`}
            >
              {doneCount}/{STEPS.length} · {pct}%
            </span>
          </div>
          <div className="flex items-center gap-1" data-drag-ignore>
            <button
              type="button"
              onClick={togglePin}
              data-testid="quickstart-pin-toggle"
              className={cn(
                'flex h-7 w-7 items-center justify-center rounded-sm hover:bg-secondary',
                pinned ? 'text-primary' : 'text-muted-foreground',
              )}
              title={pinned ? 'Unpin' : 'Pin to workspace'}
            >
              {pinned ? <Pin className="h-3.5 w-3.5" /> : <PinOff className="h-3.5 w-3.5" />}
            </button>
            <button
              type="button"
              onClick={resetProgress}
              data-testid="quickstart-reset"
              className="flex h-7 w-7 items-center justify-center rounded-sm text-muted-foreground hover:bg-secondary hover:text-foreground"
              title="Reset progress"
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={onClose}
              data-testid="quickstart-close"
              className="flex h-7 w-7 items-center justify-center rounded-sm text-muted-foreground hover:bg-secondary hover:text-foreground"
              aria-label="Close"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </header>

        {/* Progress bar + next-step CTA */}
        <div className="shrink-0 border-b border-panel-border bg-panel-header/40 px-3 py-2.5">
          <div className="h-1 overflow-hidden rounded-sm bg-secondary">
            <div
              className="h-full bg-gradient-to-r from-primary to-primary/70 transition-all"
              style={{ width: `${pct}%` }}
              data-testid="quickstart-progress-bar"
            />
          </div>
          {nextStep && (
            <div className="mt-2 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Up next</div>
                <div className="truncate text-[12px] font-semibold" data-testid="quickstart-next-step">
                  {nextStep.title}
                </div>
              </div>
              {nextStep.cta && (
                <button
                  type="button"
                  onClick={() => nextStep.cta.action(handlers)}
                  data-testid="quickstart-next-cta"
                  className="inline-flex shrink-0 items-center gap-1 rounded-md bg-primary px-2.5 py-1.5 text-[11.5px] font-semibold text-primary-foreground hover:bg-primary/90"
                >
                  <Sparkles className="h-3 w-3" />
                  {nextStep.cta.label}
                </button>
              )}
            </div>
          )}
          {!nextStep && (
            <div className="mt-2 text-[11.5px] font-semibold text-emerald-700" data-testid="quickstart-all-done">
              All steps complete — you&apos;re ready to publish!
            </div>
          )}
        </div>

        {/* Step list */}
        <ol className="scroll-thin flex-1 space-y-1.5 overflow-y-auto p-3" data-testid="quickstart-step-list">
          {STEPS.map((step, i) => {
            const done = isDone(step);
            const expanded = state.expanded === step.id;
            const autoLocked = done && step.auto && step.auto(ctx) && !state.manual[step.id];
            return (
              <li
                key={step.id}
                data-testid={`quickstart-step-${step.id}`}
                className={cn(
                  'rounded-md border transition-colors',
                  done ? 'border-emerald-300/70 bg-emerald-50/40' : 'border-panel-border bg-card',
                )}
              >
                <div className="flex items-start gap-2 p-2.5">
                  <button
                    type="button"
                    onClick={() => toggleDone(step.id)}
                    disabled={autoLocked}
                    data-testid={`quickstart-check-${step.id}`}
                    className={cn(
                      'mt-0.5 shrink-0',
                      done ? 'text-emerald-700' : 'text-muted-foreground hover:text-primary',
                      autoLocked && 'cursor-not-allowed opacity-70',
                    )}
                    title={autoLocked ? 'Detected as complete automatically' : (done ? 'Mark not-done' : 'Mark done')}
                  >
                    {done ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-4 w-4" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => setExpanded(step.id)}
                    className="flex-1 text-left"
                    data-testid={`quickstart-expand-${step.id}`}
                  >
                    <div className="flex items-center gap-1">
                      <span className="text-[10.5px] font-mono text-muted-foreground">{String(i + 1).padStart(2, '0')}</span>
                      <span className={cn('text-[12.5px] font-semibold', done && 'text-emerald-800')}>
                        {step.title}
                      </span>
                      {expanded
                        ? <ChevronDown className="ml-auto h-3.5 w-3.5 text-muted-foreground" />
                        : <ChevronRight className="ml-auto h-3.5 w-3.5 text-muted-foreground" />}
                    </div>
                    {expanded && (
                      <div className="mt-1.5 space-y-2 text-[11.5px] leading-relaxed text-muted-foreground">
                        <p>{step.body}</p>
                        {step.cta && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); step.cta.action(handlers); }}
                            data-testid={`quickstart-cta-${step.id}`}
                            className="inline-flex items-center gap-1 rounded-sm border border-panel-border bg-background px-2 py-1 text-[11px] font-semibold text-foreground hover:bg-secondary"
                          >
                            {step.cta.label} <ExternalLink className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    )}
                  </button>
                </div>
              </li>
            );
          })}
        </ol>

        <footer className="shrink-0 border-t border-panel-border bg-panel-header/40 px-3 py-2 text-[10.5px] text-muted-foreground">
          <span data-testid="quickstart-pin-hint">
            {pinned
              ? 'Pinned — the guide stays on top while you work. Drag the header to move · corner to resize.'
              : 'Tip: pin the guide, drag the header to reposition, or drag the corner to resize.'}
          </span>
        </footer>
        {/* iter55g2 — Bottom-right resize handle. */}
        <div
          data-testid="quickstart-resize-handle"
          onPointerDown={onResizePointerDown}
          onPointerMove={onResizePointerMove}
          onPointerUp={onResizePointerUp}
          onPointerCancel={onResizePointerUp}
          title="Drag to resize"
          className="absolute bottom-0 right-0 h-4 w-4 cursor-nwse-resize"
          style={{ touchAction: 'none' }}
        >
          <svg viewBox="0 0 12 12" className="h-full w-full text-muted-foreground">
            <path d="M11 4 L4 11 M11 8 L8 11" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" />
          </svg>
        </div>
      </aside>
    </>
  );
}
